/**
 * Admin modules: clinics, clinicAccess, users, doctors, staff,
 * audit, errorLogs, analytics, security, inventory, profile, ai,
 * reports, queue, schedule, schedules, doctorStatus.
 */
import { fetchApi, API_BASE_URL } from '../client';
import { secureStorage } from '../../secureStorage';
import { buildQS } from '../helpers';
import type {
  User, Clinic, Doctor, StaffMember, AuditLog, Profile,
  ProfileUpdateRequest, UploadAvatarResponse, QueueItem,
  ScheduleSlot, DoctorStatusEvent, CreateDoctorStatusEventPayload,
  InventoryItem, InventoryConfig, InventoryCreateRequest, InventoryUpdateRequest,
  StaffInviteRequest, StaffStatusUpdateRequest, AuditLogsResponse,
  AnalyticsDashboardResponse, DoctorDashboardResponse, ClinicAdminDashboardResponse,
  DoctorStats, QueueStatusUpdateRequest,
  Visit, DoctorClinic,
} from '../../../types';
import type { UserSecuritySettings, SetupPinRequest, VerifyPinRequest, PinVerificationResponse, SecuritySettingsRequest } from '../../../types/security';

export const clinicsModule = {
  getAll: () => fetchApi<Clinic[]>('/api/clinics'),
  get: (id: string) => fetchApi<Clinic>(`/api/clinics/${id}`),
  create: (data: Partial<Clinic>) =>
    fetchApi<Clinic>('/api/clinics', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Clinic>) =>
    fetchApi<Clinic>(`/api/clinics/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  createWithAdmin: async (data: {
    clinic: Partial<Clinic> & {
      subscriptionPlanId: string;
      billingCycle?: 'MONTHLY' | 'ANNUAL';
      maxDoctorsOverride?: number;
      maxStaffOverride?: number;
    };
    admin: { name: string; email: string; mobile: string };
  }) => {
    const res = await fetchApi<{ clinic: Clinic; tempPassword: string }>('/api/clinics/with-admin', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (res.error) throw new Error(res.error);
    return res.data!;
  },

  // includeTestData surfaces Test Data Studio tenants (hidden by default) so the Access Management
  // screen can enable modules (AI, etc.) on a generated test clinic.
  getClinics: async (includeTestData = false) => {
    const res = await fetchApi<Clinic[]>(`/api/clinics${includeTestData ? '?includeTestData=true' : ''}`);
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },

  resendInvitation: async (clinicId: string) => {
    const res = await fetchApi<{ message: string; expiresAt: string }>(
      `/api/clinics/${clinicId}/resend-invitation`,
      { method: 'POST' }
    );
    if (res.error) throw new Error(res.error);
    return res.data!;
  },

  deactivate: async (clinicId: string) => {
    const res = await fetchApi<{ message: string }>(`/api/clinics/${clinicId}/deactivate`, { method: 'POST' });
    if (res.error) throw new Error(res.error);
    return res.data!;
  },

  activate: async (clinicId: string) => {
    const res = await fetchApi<{ message: string }>(`/api/clinics/${clinicId}/activate`, { method: 'POST' });
    if (res.error) throw new Error(res.error);
    return res.data!;
  },
};

export const clinicAccessModule = {
  getModules: (clinicId: string) =>
    fetchApi<Array<{ code: string; name: string; description: string; enabled: boolean }>>(
      `/api/admin/clinics/${clinicId}/modules`
    ),

  setModule: (clinicId: string, moduleCode: string, enabled: boolean) =>
    fetchApi<{ message: string }>(`/api/admin/clinics/${clinicId}/modules/${moduleCode}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  getRoleMatrix: (clinicId: string) =>
    fetchApi<Record<string, Array<{
      code: string; name: string; moduleCode: string;
      defaultGranted: boolean; overrideGranted: boolean | null; effectiveGranted: boolean;
    }>>>(`/api/admin/clinics/${clinicId}/role-permissions`),

  setRolePermission: (clinicId: string, roleCode: string, permissionCode: string, granted: boolean) =>
    fetchApi<{ message: string }>(
      `/api/admin/clinics/${clinicId}/role-permissions/${roleCode}/${permissionCode}`,
      { method: 'PUT', body: JSON.stringify({ granted }) }
    ),

  removeRolePermission: (clinicId: string, roleCode: string, permissionCode: string) =>
    fetchApi<{ message: string }>(
      `/api/admin/clinics/${clinicId}/role-permissions/${roleCode}/${permissionCode}`,
      { method: 'DELETE' }
    ),
};

export const usersModule = {
  getAll: () => fetchApi<User[]>('/api/users'),
  getDoctors: () => fetchApi<User[]>('/api/users?role=doctor'),
  getByIds: (ids: string[]) => fetchApi<User[]>(`/api/users?ids=${ids.join(',')}`),
};

export const doctorsModule = {
  getAll: () => fetchApi<Doctor[]>('/api/doctors'),
  create: (data: Partial<Doctor>) =>
    fetchApi<Doctor>('/api/doctors', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Doctor>) =>
    fetchApi<Doctor>(`/api/doctors/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deactivate: (id: string) =>
    fetchApi<Doctor>(`/api/doctors/${id}/deactivate`, { method: 'PATCH' }),
  getStats: (doctorId: string, period: 'today' | 'week' | 'month' = 'today') =>
    fetchApi<DoctorStats>(`/api/doctors/${doctorId}/stats?period=${period}`),
  // Multi-clinic support
  listClinics: (userId: string) =>
    fetchApi<DoctorClinic[]>(`/api/v1/doctors/${userId}/clinics`),
  availableClinics: (userId: string) =>
    fetchApi<{ clinicId: string; clinicName: string }[]>(`/api/v1/doctors/${userId}/clinics/available`),
  assignToClinic: (userId: string, clinicId: string) =>
    fetchApi<{ doctorProfileId: string; userId: string; clinicId: string; message: string }>(
      `/api/v1/doctors/${userId}/clinics`,
      { method: 'POST', body: JSON.stringify({ clinicId }) }
    ),
  removeFromClinic: (userId: string, clinicId: string) =>
    fetchApi<{ message: string }>(
      `/api/v1/doctors/${userId}/clinics/${clinicId}`,
      { method: 'DELETE' }
    ),
};

export const staffModule = {
  getAll: () => fetchApi<StaffMember[]>('/api/staff'),
  invite: (data: StaffInviteRequest) =>
    fetchApi<StaffMember>('/api/staff/invite', { method: 'POST', body: JSON.stringify(data) }),
  getInvitations: () => fetchApi('/api/staff/invitations'),
  resendInvitation: (invitationId: string) =>
    fetchApi(`/api/staff/invitations/${invitationId}/resend`, { method: 'POST' }),
  updateStatus: (id: string, isActive: boolean) =>
    fetchApi<StaffMember>(`/api/staff/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive } as StaffStatusUpdateRequest),
    }),
  updateRole: (id: string, role: string) =>
    fetchApi<StaffMember>(`/api/staff/${id}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
};

export const auditModule = {
  getLogs: (limit = 50, clinicId?: string, page = 0) =>
    fetchApi<AuditLog[] | AuditLogsResponse>(
      `/api/audit/logs${buildQS({ limit, clinicId, page })}`
    ),
};

export const errorLogsModule = {
  getLogs: (options?: {
    limit?: number; clinicId?: string; startDate?: string; endDate?: string;
    level?: string; statusCode?: number; search?: string;
  }) => {
    const params = new URLSearchParams();
    params.append('limit', String(options?.limit ?? 100));
    if (options?.clinicId) params.append('clinicId', options.clinicId);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.level) params.append('level', options.level);
    if (options?.statusCode) params.append('statusCode', String(options.statusCode));
    if (options?.search) params.append('search', options.search);
    return fetchApi<any[]>(`/api/admin/error-logs?${params.toString()}`);
  },
  getById: (id: number) => fetchApi<any>(`/api/admin/error-logs/${id}`),
};

export const analyticsModule = {
  getDashboard: (range?: number) =>
    fetchApi<AnalyticsDashboardResponse>(
      `/api/analytics/dashboard${range ? `?range=${range}` : ''}`
    ),
  getDoctorDashboard: (range = 30) =>
    fetchApi<DoctorDashboardResponse>(`/api/analytics/doctor-dashboard?range=${range}`),
  getClinicAdminDashboard: (range = 30) =>
    fetchApi<ClinicAdminDashboardResponse>(`/api/analytics/clinic-admin-dashboard?range=${range}`),
};

export const securityModule = {
  getSettings: () =>
    fetchApi<UserSecuritySettings>('/api/user-security/settings'),
  setupPin: (data: SetupPinRequest) =>
    fetchApi<UserSecuritySettings>('/api/user-security/setup-pin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  verifyPin: (data: VerifyPinRequest) =>
    fetchApi<PinVerificationResponse>('/api/user-security/verify-pin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateSettings: (data: SecuritySettingsRequest) =>
    fetchApi<UserSecuritySettings>('/api/user-security/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  resetPin: () =>
    fetchApi<void>('/api/user-security/reset-pin', { method: 'POST' }),
  resetFailedAttempts: (targetUserId?: string) =>
    fetchApi<void>(
      `/api/user-security/reset-failed-attempts${targetUserId ? `?targetUserId=${targetUserId}` : ''}`,
      { method: 'POST' }
    ),
};

export const inventoryModule = {
  getAll: () => fetchApi<InventoryItem[]>('/api/admin/inventory'),
  getConfig: () => fetchApi<InventoryConfig>('/api/admin/inventory/config'),
  updateConfig: (config: InventoryConfig) =>
    fetchApi<InventoryConfig>('/api/admin/inventory/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
  create: (item: InventoryCreateRequest) =>
    fetchApi<InventoryItem>('/api/admin/inventory', {
      method: 'POST',
      body: JSON.stringify(item),
    }),
  update: (id: string, item: InventoryUpdateRequest) =>
    fetchApi<InventoryItem>(`/api/admin/inventory/${id}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    }),
};

export const profileModule = {
  getMyProfile: () => fetchApi<Profile>('/api/profile/me'),
  updateMyProfile: (data: ProfileUpdateRequest) =>
    fetchApi<Profile>('/api/profile/me', { method: 'PUT', body: JSON.stringify(data) }),
  uploadAvatar: async (file: File) => {
    const token = secureStorage.getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/api/profile/avatar`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to upload avatar');
    return response.json() as Promise<UploadAvatarResponse>;
  },
};

export const aiModule = {
  getPatientSummary: (patientId: string) =>
    fetchApi<string>(`/api/ai/patient-summary/${patientId}`),
  getLabTrends: (patientId: string) =>
    fetchApi<any>(`/api/ai/lab-trends/${patientId}`),
  checkPrescriptionSafety: (patientId: string, prescriptionData: any) =>
    fetchApi<any>(`/api/ai/prescription-safety-check/${patientId}`, {
      method: 'POST',
      body: JSON.stringify(prescriptionData),
    }),
  polishNotes: (notes: string, type: 'chief_complaint' | 'clinical_note') =>
    fetchApi<{ original: string; polished: string }>('/api/ai/polish-notes', {
      method: 'POST',
      body: JSON.stringify({ notes, type }),
    }),
  analyzeLabReport: (filePath: string, patientId: string) =>
    fetchApi<any>('/api/ai/analyze-lab', {
      method: 'POST',
      body: JSON.stringify({ filePath, patientId }),
    }),
};

export const reportsModule = {
  getDailyReport: (date: string) =>
    fetchApi<{
      total_visits: number; total_revenue: number;
      cash_amount: number; card_amount: number; upi_amount: number;
      visits: Visit[];
    }>(`/api/reports/daily?date=${date}`),
};

export const queueModule = {
  getTodayQueue: () => fetchApi<QueueItem[]>('/api/queue/today'),
  updateStatus: (id: string, status: QueueItem['status']) =>
    fetchApi<QueueItem>(`/api/queue/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status } as QueueStatusUpdateRequest),
    }),
};

export const scheduleModule = {
  getSchedule: (doctorId?: string, date?: string) =>
    fetchApi<ScheduleSlot[]>(`/api/doctor/schedule${buildQS({ doctorId, date })}`),
  createSlot: (data: { doctorId: string; date: string; startTime: string; endTime: string }) =>
    fetchApi<ScheduleSlot>('/api/doctor/schedule', { method: 'POST', body: JSON.stringify(data) }),
  blockSlot: (slotId: string, reason = 'Staff request') =>
    fetchApi<ScheduleSlot>(`/api/doctor/schedule/${slotId}/block`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    }),
  unblockSlot: (slotId: string) =>
    fetchApi<ScheduleSlot>(`/api/doctor/schedule/${slotId}/unblock`, { method: 'PATCH' }),
};

export const schedulesModule = {
  getConfig: (doctorId: string) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/schedule-config`),
  saveConfig: (doctorId: string, config: any) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/schedule-config`, {
      method: 'POST',
      body: JSON.stringify(config),
    }),
  activate: (doctorId: string) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/schedule-config/activate`, { method: 'PATCH' }),
  deactivate: (doctorId: string) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/schedule-config/deactivate`, { method: 'PATCH' }),
  getSlots: (doctorId: string, options?: { startDate?: string; endDate?: string }) => {
    if (!options?.startDate || !options?.endDate) {
      const today = new Date();
      const end = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      return fetchApi<any>(
        `/api/v1/doctors/${doctorId}/slots/range?startDate=${today.toISOString().split('T')[0]}&endDate=${end.toISOString().split('T')[0]}`
      );
    }
    return fetchApi<any>(
      `/api/v1/doctors/${doctorId}/slots/range?startDate=${options.startDate}&endDate=${options.endDate}`
    );
  },
  getSlotsByDate: (doctorId: string, date: string) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/slots?date=${date}`),
  getAppointments: (doctorId: string, date?: string) => {
    const d = date || new Date().toISOString().split('T')[0];
    return fetchApi<any>(`/api/appointments?doctorId=${doctorId}&date=${d}`);
  },
};

export const doctorStatusModule = {
  list: (doctorId?: string, start?: string, end?: string) =>
    fetchApi<DoctorStatusEvent[]>(`/api/doctor/status-events${buildQS({ doctorId, start, end })}`),
  current: (doctorId?: string) =>
    fetchApi<DoctorStatusEvent>(
      `/api/doctor/status-events/current${doctorId ? `?doctorId=${doctorId}` : ''}`
    ),
  create: (payload: CreateDoctorStatusEventPayload) =>
    fetchApi<DoctorStatusEvent>('/api/doctor/status-events', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};
